import { formatISO } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

import { Language } from './language';
import { utcDateToLocalDate } from './utcDateToLocalDate';

type DateInput = string | Date | null | undefined;

const format = (dateInput: DateInput, options: Intl.DateTimeFormatOptions = {}): string => {
  const date = parseDateInput(dateInput);
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat(Language.DEFAULT, options).format(date);
};

const parseDateInput = (input: DateInput): Date | null => {
  if (!input) {
    return null;
  }

  if (typeof input === 'string') {
    const shortDateMatch = /^(\d{2})-(\d{4})$/.exec(input);
    if (shortDateMatch) {
      const [, month, year] = shortDateMatch;
      return new Date(`${year}-${month}-01`);
    }

    return new Date(input);
  }

  return input;
};

const formatDateYear = (dateInput: DateInput): string =>
  format(dateInput, {
    year: 'numeric'
  });

const formatDateMonth = (dateInput: DateInput): string =>
  format(dateInput, {
    month: 'long'
  });

const formatDateMonthShort = (dateInput: DateInput): string =>
  format(dateInput, {
    month: 'short'
  });

const formatDateDay = (dateInput: DateInput): string => format(dateInput, { day: 'numeric' });

const formatDateFullMonthDayYear = (dateInput: DateInput): string =>
  format(dateInput, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

const formatDateYearMonth = (dateInput: DateInput, options?: { separator?: '-' | ' ' }): string => {
  const separator = options?.separator ?? ' ';
  const formattedDate = format(dateInput, {
    year: 'numeric',
    month: 'short'
  });

  if (separator === '-') {
    return formattedDate.replace(' ', '-');
  }

  return formattedDate;
};

const formatDateYearFullMonth = (dateInput: DateInput): string =>
  format(dateInput, {
    year: 'numeric',
    month: 'long'
  });

const formatDateMonthDay = (dateInput: DateInput): string =>
  format(dateInput, {
    month: 'short',
    day: 'numeric'
  });

/** Returns "MMM-yy" format, e.g. "Jun-24". Used for chart axis labels. */
const formatMonthShortYear = (dateInput: DateInput): string => {
  if (!dateInput) return '';
  const d = dateInput instanceof Date ? dateInput : utcDateToLocalDate(dateInput);
  const shortMonth = d.toLocaleString('en-US', { month: 'short' });
  const shortYear = d.getFullYear().toString().slice(-2);
  return `${shortMonth}-${shortYear}`;
};

const formatDateYearMonthDay = (dateInput: DateInput): string =>
  format(dateInput, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });

const formatDateYearMonthDayTime = (dateInput: DateInput, timeZone?: string): string =>
  format(dateInput, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone
  });

const formatDateTime = (dateInput: DateInput, timeZone?: string): string =>
  format(dateInput, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
    timeZone
  });

const formatDateISO = (dateInput: DateInput): string => {
  const parsedDate = parseDateInput(dateInput);
  if (!parsedDate) return '';

  return formatISO(parsedDate, { representation: 'date' });
};

/**
 * Asserts wether if a given value is a string.
 *
 * @param {unknown} value - The value to be checked.
 * @return {boolean} Returns true if the value is a string, false otherwise.
 */
export const isString = (value: unknown): value is string =>
  ({}).toString.call(value) === '[object String]'; // NOSONAR

/**
 * Checks if a value is of type Date.
 *
 * @param {any} value - The value to be checked.
 * @return {boolean} Returns true if the value is an instance of Date, otherwise returns false.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- input of any type
function isDate(value: any): value is Date {
  return value instanceof Date;
}

/**
 * Checks if the given value is a serialized date.
 *
 * @param {unknown} value - The value to check.
 * @return {boolean} Returns true if the value is a serialized date, otherwise false.
 */
const isSerializedDate = (value: unknown): boolean => {
  // eslint-disable-next-line security/detect-unsafe-regex -- false positive: all quantifiers are bounded, no backtracking blowup; safe-regex's star-height heuristic rejects any quantifier inside an optional group
  const datePattern = /^\d{4}-\d{2}-\d{2}(T0{2}:0{2}:0{2,3}(\.0{1,9})?Z?)?$/;
  return isString(value) && datePattern.test(value);
};

/**
 * Checks if the given value is a serialized date and time string.
 *
 * @param {unknown} value - The value to check.
 * @return {boolean} Returns true if the value is a serialized date and time string, otherwise false.
 */
const isSerializedDateTime = (value: unknown): boolean => {
  // eslint-disable-next-line security/detect-unsafe-regex -- false positive: all quantifiers are bounded, no backtracking blowup
  const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2,3}(\.\d{1,9})?Z?)$/;
  return isString(value) && datePattern.test(value);
};

/**
 * A reviver function that converts serialized dates and date-times
 * to native JavaScript Date objects.
 *
 * @param {any} value - The JSON string to revive.
 * @return {any} - The revived date object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- input of any type
function reviveDate(value: any) {
  if (!value) return;
  return JSON.parse(JSON.stringify(value), (_key: string, value: unknown) => {
    if (isSerializedDate(value)) {
      return utcDateToLocalDate(value as string);
    }
    if (isSerializedDateTime(value)) {
      return new Date(value as string);
    }
    return value;
  });
}

/**
 * Checks if the given value represents a time at midnight.
 *
 * @param {Date} value - The value to check.
 * @return {boolean} Returns true if the value represents a time at midnight, false otherwise.
 */
function isTimeAtMidnight(value: Date): boolean {
  return value.getHours() === 0 && value.getMinutes() === 0 && value.getSeconds() === 0;
}

/**
 * Sanitizes a given date by formatting it into the "YYYY-MM-DD" format.
 *
 * @param {Date} date - The date to be sanitized.
 * @return {string} The sanitized date in the "YYYY-MM-DD" format.
 */
function sanitizeDate(date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Serializes a date object into a JSON string.
 *
 * @param {unknown} data - The date object to be serialized.
 * @return {unknown} The serialized JSON string.
 */
function serializeDate(data: unknown) {
  return JSON.parse(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- input of any type
    JSON.stringify(data, function (this: any, key: string, value: any): any {
      if (isDate(this[key])) {
        return isTimeAtMidnight(this[key]) ? sanitizeDate(this[key]) : this[key].toISOString();
      }
      return value;
    })
  );
}

const getCurrDateString = (time: string) => {
  const curDate = new Date();
  return `${curDate.getFullYear()}-${(curDate.getMonth() + 1).toString().padStart(2, '0')}-${curDate
    .getDate()
    .toString()
    .padStart(2, '0')}T${time}`;
};

// The converters accept strict 24h HH:mm:ss only. Anything else ('8:00 AM',
// '25:00:00') degrades to '' like empty input — native Date would build an
// Invalid Date that formatInTimeZone throws on, while date-fns-tz's parser
// would silently misread it, so neither can be left to the parser.
const isStrictTime = (time: string) => /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(time);

const convertUTCToCentralTime = (time: string) => {
  if (!isStrictTime(time)) return '';
  const date = new Date(`${getCurrDateString(time)}Z`);
  if (Number.isNaN(date.getTime())) return '';
  return formatInTimeZone(date, 'America/Chicago', 'HH:mm:ss');
};

const convertCentralTimeToUTC = (time: string) => {
  if (!isStrictTime(time)) return '';
  const date = zonedTimeToUtc(getCurrDateString(time), 'America/Chicago');
  if (Number.isNaN(date.getTime())) return '';
  return formatInTimeZone(date, 'UTC', 'HH:mm:ss');
};

function formatRelativeDate(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const n = Math.floor(diffDays / 7);
    return `${n} ${n === 1 ? 'week' : 'weeks'} ago`;
  }
  if (diffDays < 365) {
    const n = Math.floor(diffDays / 30);
    return `${n} ${n === 1 ? 'month' : 'months'} ago`;
  }
  const n = Math.floor(diffDays / 365);
  return `${n} ${n === 1 ? 'year' : 'years'} ago`;
}

export {
  formatDateYear,
  formatDateDay,
  formatDateMonth,
  formatDateMonthShort,
  formatDateMonthDay,
  formatMonthShortYear,
  formatDateYearMonth,
  formatDateYearFullMonth,
  formatDateFullMonthDayYear,
  formatDateYearMonthDay,
  formatDateYearMonthDayTime,
  formatDateISO,
  isSerializedDate,
  reviveDate,
  serializeDate,
  sanitizeDate,
  convertUTCToCentralTime,
  convertCentralTimeToUTC,
  formatDateTime,
  formatRelativeDate
};
